import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { resolveMediaUrl } from "../../../lib/media-url";
import type { Question } from "../types";

/**
 * DUO chrome question card — white card with cue media + prompt text.
 */
export type DuoQuestionCardProps = {
  question: Question;
  imageAltTexts?: string[];
  audioFailed?: boolean;
  onRetryAudio?: () => void;
};

export function DuoQuestionCard({
  question,
  imageAltTexts,
  audioFailed,
  onRetryAudio,
}: DuoQuestionCardProps) {
  const {
    audioRef,
    audioPlaying,
    audioBlocked,
    audioProgress,
    toggleAudio,
    replay,
    forward,
  } = useQuestionAudio(question.questionAudioUrl);

  const partLabel = (() => {
    switch (question.part) {
      case 2:
        return "Gebruik het plaatje.";
      case 3:
        return "Kies een van de plaatjes.";
      case 4:
        return "Gebruik alle plaatjes.";
      default:
        return null;
    }
  })();

  return (
    <article className="mx-auto w-full max-w-[1200px] flex min-h-[clamp(320px,48vh,560px)] flex-col rounded-lg border border-[var(--duo-card-border)] bg-[var(--duo-card)] p-10 shadow-[var(--duo-card-shadow)]">
      {question.part === 1 ? (
        <D1Text
          promptText={question.promptText}
          audioPlaying={audioPlaying}
          audioProgress={audioProgress}
          onPlay={toggleAudio}
          onReplay={replay}
          onForward={forward}
        />
      ) : (
        <>
          <DnPhotos
            imageUrls={question.imageUrls}
            imageAltTexts={imageAltTexts}
            part={question.part}
            audioPlaying={audioPlaying}
            audioProgress={audioProgress}
            onPlay={toggleAudio}
            onReplay={replay}
            onForward={forward}
          />
          <div className="mt-6 space-y-3">
            <p className="text-base leading-[1.5] text-[var(--duo-text)]">
              {question.promptText}
            </p>
            {partLabel ? (
              <p className="text-sm italic text-[var(--duo-text)]">{partLabel}</p>
            ) : null}
          </div>
        </>
      )}

      {!question.questionAudioUrl && !audioFailed ? (
        <p
          role="status"
          aria-live="polite"
          className="mt-4 text-xs italic text-[var(--duo-text-muted)]"
        >
          Audio wordt voorbereid…
        </p>
      ) : null}
      {!question.questionAudioUrl && audioFailed ? (
        <p
          role="status"
          className="mt-4 flex items-center gap-2 text-xs italic text-[var(--duo-text-muted)]"
        >
          <span>Audio is nu niet beschikbaar — lees de vraag hierboven.</span>
          {onRetryAudio ? (
            <button
              type="button"
              onClick={onRetryAudio}
              className="text-[var(--duo-blue)] underline-offset-2 hover:underline"
            >
              Probeer opnieuw
            </button>
          ) : null}
        </p>
      ) : null}
      {audioBlocked && question.questionAudioUrl ? (
        <p className="mt-4 text-xs italic text-[var(--duo-text-muted)]">
          Tik op ▶ om de vraag te horen.
        </p>
      ) : null}

      {question.questionAudioUrl ? (
        <audio
          ref={audioRef}
          src={resolveMediaUrl(question.questionAudioUrl)}
          playsInline
          preload="auto"
          className="sr-only"
          aria-hidden="true"
        />
      ) : null}
    </article>
  );
}

function D1Text({
  promptText,
  audioPlaying,
  audioProgress,
  onPlay,
  onReplay,
  onForward,
}: {
  promptText: string;
  audioPlaying: boolean;
  audioProgress: number;
  onPlay: () => void;
  onReplay: () => void;
  onForward: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <PlaybackControls
          audioPlaying={audioPlaying}
          audioProgress={audioProgress}
          onPlay={onPlay}
          onReplay={onReplay}
          onForward={onForward}
        />
      </div>
      <p className="text-base leading-[1.5] text-[var(--duo-text)]">{promptText}</p>
    </div>
  );
}

function DnPhotos({
  imageUrls,
  imageAltTexts,
  part,
  audioPlaying,
  audioProgress,
  onPlay,
  onReplay,
  onForward,
}: {
  imageUrls: string[];
  imageAltTexts?: string[];
  part: number;
  audioPlaying: boolean;
  audioProgress: number;
  onPlay: () => void;
  onReplay: () => void;
  onForward: () => void;
}) {
  const gridCols =
    part === 2 ? "grid-cols-1" : part === 3 ? "grid-cols-2" : "grid-cols-3";
  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <PlaybackControls
          audioPlaying={audioPlaying}
          audioProgress={audioProgress}
          onPlay={onPlay}
          onReplay={onReplay}
          onForward={onForward}
        />
      </div>
      {imageUrls.length > 0 ? (
        <div className={`grid place-items-start justify-start gap-3 ${gridCols}`}>
          {imageUrls.map((u, i) => (
            <img
              key={u}
              src={resolveMediaUrl(u)}
              alt={imageAltTexts?.[i] ?? `Foto ${i + 1}`}
              loading="lazy"
              className="aspect-[3/2] w-full max-w-[450px] rounded-md object-cover"
            />
          ))}
        </div>
      ) : (
        <div className="grid h-[300px] w-[450px] max-w-full grid-cols-1 place-items-center rounded-md bg-[var(--duo-grey-cell)] text-sm text-[var(--duo-text-muted)]">
          Foto's laden…
        </div>
      )}
    </div>
  );
}

function PlaybackControls({
  audioPlaying,
  audioProgress,
  onPlay,
  onReplay,
  onForward,
}: {
  audioPlaying: boolean;
  audioProgress: number;
  onPlay: () => void;
  onReplay: () => void;
  onForward: () => void;
}) {
  return (
    <div className="inline-flex items-center gap-2">
      <PlaybackButton ariaLabel="10 seconden terug" onClick={onReplay}>
        <Replay10Icon />
      </PlaybackButton>
      <PlayWithProgress
        progress={audioProgress}
        playing={audioPlaying}
        onClick={onPlay}
      />
      <PlaybackButton ariaLabel="10 seconden vooruit" onClick={onForward}>
        <Forward10Icon />
      </PlaybackButton>
    </div>
  );
}

function PlayWithProgress({
  progress,
  playing,
  onClick,
}: {
  progress: number;
  playing: boolean;
  onClick: () => void;
}) {
  const arcDeg = Math.max(0, Math.min(1, progress)) * 360;
  return (
    <div
      className="relative inline-flex h-9 w-9 items-center justify-center rounded-full"
      style={{
        background: `conic-gradient(var(--duo-orange) 0deg ${arcDeg}deg, var(--duo-grey-cell) ${arcDeg}deg 360deg)`,
        transition: "background 100ms linear",
      }}
    >
      <span
        aria-hidden="true"
        className="absolute inset-[3px] rounded-full bg-[var(--duo-card)]"
      />
      <button
        type="button"
        onClick={onClick}
        aria-label={playing ? "Pauze" : "Speel af"}
        className="relative inline-flex h-7 w-7 items-center justify-center rounded-full text-[var(--duo-orange)] transition-colors hover:bg-[var(--duo-grey-cell)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-orange)] focus-visible:outline-offset-2"
      >
        {playing ? (
          <Pause size={16} strokeWidth={0} fill="currentColor" aria-hidden="true" />
        ) : (
          <Play size={16} strokeWidth={0} fill="currentColor" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

function Replay10Icon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={20}
      height={20}
      viewBox="0 -960 960 960"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M480-80q-75 0-140.5-28.5t-114-77q-48.5-48.5-77-114T120-440h80q0 117 81.5 198.5T480-160q117 0 198.5-81.5T760-440q0-117-81.5-198.5T480-720h-6l62 62-56 58-160-160 160-160 56 58-62 62h6q75 0 140.5 28.5t114 77q48.5 48.5 77 114T840-440q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T480-80ZM360-320v-180h-60v-60h120v240h-60Zm140 0q-17 0-28.5-11.5T460-360v-160q0-17 11.5-28.5T500-560h80q17 0 28.5 11.5T620-520v160q0 17-11.5 28.5T580-320h-80Zm20-60h40v-120h-40v120Z" />
    </svg>
  );
}

function Forward10Icon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={20}
      height={20}
      viewBox="0 -960 960 960"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M360-320v-180h-60v-60h120v240h-60Zm140 0q-17 0-28.5-11.5T460-360v-160q0-17 11.5-28.5T500-560h80q17 0 28.5 11.5T620-520v160q0 17-11.5 28.5T580-320h-80Zm20-60h40v-120h-40v120ZM480-80q-75 0-140.5-28.5t-114-77q-48.5-48.5-77-114T120-440q0-75 28.5-140.5t77-114q48.5-48.5 114-77T480-800h6l-62-62 56-58 160 160-160 160-56-58 62-62h-6q-117 0-198.5 81.5T200-440q0 117 81.5 198.5T480-160q117 0 198.5-81.5T760-440h80q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T480-80Z" />
    </svg>
  );
}

function PlaybackButton({
  children,
  ariaLabel,
  onClick,
}: {
  children: React.ReactNode;
  ariaLabel: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--duo-text-muted)] transition-colors hover:bg-[var(--duo-grey-cell)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue)] focus-visible:outline-offset-2"
    >
      {children}
    </button>
  );
}

function useQuestionAudio(audioUrl: string | null) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);

  function pauseOtherAudio() {
    if (typeof document === "undefined") return;
    document.querySelectorAll("audio").forEach((a) => {
      if (a !== audioRef.current && !a.paused) a.pause();
    });
  }

  useEffect(() => {
    setAudioBlocked(false);
    setAudioPlaying(false);
    setAudioProgress(0);
    const a = audioRef.current;
    if (!audioUrl || !a) return;
    pauseOtherAudio();
    a.play()
      .then(() => setAudioPlaying(true))
      .catch(() => setAudioBlocked(true));
    const onPlay = () => setAudioPlaying(true);
    const onPauseOrEnd = () => setAudioPlaying(false);
    const onTimeUpdate = () => {
      const dur = a.duration;
      if (Number.isFinite(dur) && dur > 0) {
        setAudioProgress(Math.max(0, Math.min(1, a.currentTime / dur)));
      }
    };
    const onEnded = () => {
      setAudioPlaying(false);
      setAudioProgress(1);
    };
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPauseOrEnd);
    a.addEventListener("ended", onEnded);
    a.addEventListener("timeupdate", onTimeUpdate);
    return () => {
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPauseOrEnd);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, [audioUrl]);

  function toggleAudio() {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      pauseOtherAudio();
      a.play()
        .then(() => {
          setAudioPlaying(true);
          setAudioBlocked(false);
        })
        .catch(() => {});
    } else {
      a.pause();
      setAudioPlaying(false);
    }
  }

  function replay() {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, a.currentTime - 10);
  }

  function forward() {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.min(a.duration || 0, a.currentTime + 10);
  }

  return {
    audioRef,
    audioPlaying,
    audioBlocked,
    audioProgress,
    toggleAudio,
    replay,
    forward,
  };
}
